import React, { useMemo, useState } from "react";
import { Sparkles, Plus, Copy, Trash2, Star, Repeat, X, AlertTriangle, ArrowRight, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtNOK, fmtPct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, AIButton, Card } from "@/components/common";
import { ProductPicker } from "@/components/opportunity/ProductPicker";
import { cn } from "@/lib/utils";

const VARIANT_HINTS = ["Recommended (balanced)", "Good - lowest cost", "Better - mid range", "Premium - best in class", "Sony-based", "Canon-based", "Panasonic-based", "Blackmagic-based"];
const GEN_STEPS = ["Reading requirements", "Matching products in catalog", "Sizing services", "Checking lead times and budget", "Writing rationale"];

function QtyInput({ value, onCommit, testId }) {
  const [v, setV] = useState(value);
  React.useEffect(() => setV(value), [value]);
  return <Input type="number" min="0" step="1" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => Number(v) !== Number(value) && onCommit(Number(v))} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} className="h-8 w-16 rounded-[8px] text-center tabular" data-testid={testId} />;
}

export function SolutionTab({ data, reload, setTab }) {
  const o = data.opportunity;
  const solutions = data.solutions || [];
  const [selectedId, setSelectedId] = useState(() => (solutions.find((s) => s.is_active) || solutions[0])?.id);
  const sol = solutions.find((s) => s.id === selectedId) || solutions.find((s) => s.is_active) || solutions[0];
  const [hint, setHint] = useState(VARIANT_HINTS[0]);
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [picker, setPicker] = useState(null); // {groupId} | {itemId}
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");

  React.useEffect(() => {
    if (!generating) return undefined;
    setGenStep(0);
    const t = setInterval(() => setGenStep((s) => Math.min(s + 1, GEN_STEPS.length - 1)), 5000);
    return () => clearInterval(t);
  }, [generating]);

  const generate = async (replace = false) => {
    if (data.requirements.length === 0 && !o.tender_id) {
      toast.error("Add requirements first - the AI designs against them.");
      setTab("requirements");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/opportunities/${o.id}/solutions/generate`, { variant_hint: hint, replace_solution_id: replace ? sol?.id : null });
      toast.success(`Solution '${r.data.name}' ready - GM ${fmtPct(r.data.totals.gm_pct)}`);
      const d = await reload();
      setSelectedId(r.data.id);
      if (!d) return;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const call = async (fn, msg) => {
    try {
      const r = await fn();
      if (msg) toast.success(msg);
      await reload();
      return r;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  };

  const totals = sol?.totals;
  const approval = totals?.approval;

  return (
    <div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap items-center gap-2" data-testid="solution-variant-tabs">
          {solutions.map((s) => (
            <button key={s.id} type="button" onClick={() => setSelectedId(s.id)} data-testid={`solution-variant-${s.id}`} className={cn("inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium hover:bg-muted", sol?.id === s.id ? "border-foreground bg-foreground text-white hover:bg-foreground" : "bg-white")}>
              {s.is_active && <Star className="h-3 w-3 fill-current" />}
              {s.name}
              <span className={cn("tabular text-xs", sol?.id === s.id ? "text-white/70" : "text-muted-foreground")}>{s.totals ? `${(s.totals.total_sales / 1e6).toFixed(2)} M` : ""}</span>
            </button>
          ))}
          <Button variant="ghost" size="sm" className="gap-1" onClick={() => call(() => api.post(`/opportunities/${o.id}/solutions`, { name: `Option ${String.fromCharCode(65 + solutions.length)}` }), "Empty variant created")} data-testid="solution-new-variant-button"><Plus className="h-4 w-4" /> Variant</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={hint} onValueChange={setHint}>
            <SelectTrigger className="h-9 w-[210px] rounded-[10px]" data-testid="solution-variant-hint-select"><SelectValue /></SelectTrigger>
            <SelectContent>{VARIANT_HINTS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
          </Select>
          <AIButton loading={generating} onClick={() => generate(false)} className="bg-primary text-white hover:bg-[#ff8e76]" data-testid="solution-generate-proposal-button">Generate solution proposal</AIButton>
        </div>
      </div>

      {generating && (
        <div className="ai-banner mt-5 space-y-1" data-testid="solution-generating">
          {GEN_STEPS.map((s, i) => (
            <div key={s} className={cn("flex items-center gap-2 text-sm", i < genStep ? "text-muted-foreground" : i === genStep ? "text-foreground" : "text-muted-foreground/50")}>
              {i < genStep ? <Check className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" /> : i === genStep ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /> : <span className="h-3.5 w-3.5 rounded-full border" />}
              {s}
            </div>
          ))}
        </div>
      )}

      {!sol ? (
        <div className="mt-8">
          <EmptyState icon={Sparkles} title="No solution yet" description={data.requirements.length ? `The AI can design a first proposal from the ${data.requirements.length} requirements - products, services and rationale.` : "Capture requirements first, then let the AI propose a solution."} action={<AIButton loading={generating} onClick={() => generate(false)} className="bg-primary text-white hover:bg-[#ff8e76]" data-testid="solution-empty-generate-button">Generate solution proposal</AIButton>} secondary={<Button variant="outline" onClick={() => call(() => api.post(`/opportunities/${o.id}/solutions`, { name: "Option A" }))}>Build manually</Button>} testId="solution-empty" />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                {renaming ? (
                  <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); call(() => api.patch(`/solutions/${sol.id}`, { name: newName }), "Renamed"); setRenaming(false); }}>
                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus className="h-8 w-56" data-testid="solution-rename-input" /><Button size="sm" type="submit" className="h-8">Save</Button>
                  </form>
                ) : (
                  <h2 className="flex items-center gap-2 text-2xl font-semibold tracking-[-0.02em]" data-testid="solution-name">{sol.name}{sol.is_active && <span className="rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">Active</span>}<button type="button" onClick={() => { setNewName(sol.name); setRenaming(true); }} className="text-muted-foreground hover:text-foreground" data-testid="solution-rename-button"><Pencil className="h-3.5 w-3.5" /></button></h2>
                )}
                {sol.headline ? <p className="mt-1 text-sm text-muted-foreground">{sol.headline}</p> : null}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {!sol.is_active && <Button size="sm" variant="outline" className="gap-1" onClick={() => call(() => api.post(`/solutions/${sol.id}/activate`), `'${sol.name}' is now the active variant`)} data-testid="solution-activate-button"><Star className="h-3.5 w-3.5" /> Set as active</Button>}
                <Button size="sm" variant="outline" className="gap-1" onClick={async () => { const r = await call(() => api.post(`/solutions/${sol.id}/duplicate`, {}), "Variant copied"); if (r) setSelectedId(r.data.id); }} data-testid="solution-duplicate-button"><Copy className="h-3.5 w-3.5" /> Duplicate</Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => generate(true)} disabled={generating} data-testid="solution-regenerate-button"><Repeat className="h-3.5 w-3.5" /> Regenerate</Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => call(() => api.post(`/solutions/${sol.id}/groups`, { name: "New group" }))} data-testid="solution-add-group-button"><Plus className="h-3.5 w-3.5" /> Group</Button>
                <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground hover:text-destructive" onClick={() => { if (window.confirm(`Delete variant '${sol.name}'?`)) { call(() => api.delete(`/solutions/${sol.id}`), "Variant deleted"); setSelectedId(null); } }} data-testid="solution-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>

            {sol.rationale ? <div className="ai-banner text-sm leading-6" data-testid="solution-rationale"><span className="font-semibold">Why this design: </span>{sol.rationale}</div> : null}
            {sol.warnings?.length > 0 && (
              <div className="space-y-2" data-testid="solution-warnings">
                {sol.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-xl border border-[hsl(var(--status-warn)/0.3)] bg-[hsl(var(--status-warn)/0.06)] px-3 py-2 text-sm"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--status-warn))]" /><span><span className="font-medium capitalize">{(w.type || "").replace("_", " ")}: </span>{w.message}</span></div>
                ))}
              </div>
            )}

            {sol.groups.length === 0 && <EmptyState title="Empty variant" description="Add a group and products, or regenerate with AI." action={<Button variant="outline" onClick={() => call(() => api.post(`/solutions/${sol.id}/groups`, { name: "Camera system" }))}>Add first group</Button>} />}

            {sol.groups.map((g) => {
              const gt = totals?.groups?.find((x) => x.group_id === g.id);
              return (
                <Card key={g.id} className="overflow-hidden" data-testid={`solution-group-${g.id}`}>
                  <div className="flex items-center justify-between gap-3 border-b bg-muted/40 px-4 py-2.5">
                    <input defaultValue={g.name} onBlur={(e) => e.target.value !== g.name && call(() => api.patch(`/solutions/${sol.id}/groups/${g.id}`, { name: e.target.value }))} className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none" data-testid="solution-group-name-input" />
                    <span className="tabular text-sm text-muted-foreground">{gt ? fmtNOK(gt.sales) : ""}</span>
                    <button type="button" onClick={() => setPicker({ groupId: g.id })} className="rounded-md p-1 text-muted-foreground hover:bg-white hover:text-foreground" title="Add product" data-testid="solution-group-add-item-button"><Plus className="h-4 w-4" /></button>
                    <button type="button" onClick={() => call(() => api.delete(`/solutions/${sol.id}/groups/${g.id}`))} className="rounded-md p-1 text-muted-foreground hover:bg-white hover:text-destructive" title="Remove group" data-testid="solution-group-delete-button"><X className="h-4 w-4" /></button>
                  </div>
                  <ul className="divide-y">
                    {g.items.length === 0 && <li className="px-4 py-3 text-sm text-muted-foreground">No items - <button type="button" className="underline" onClick={() => setPicker({ groupId: g.id })}>add a product</button>.</li>}
                    {g.items.map((it) => (
                      <li key={it.id} className="group flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`solution-item-${it.id}`}>
                        <QtyInput value={it.qty} onCommit={(q) => call(() => api.patch(`/solutions/${sol.id}/items/${it.id}`, { qty: q }))} testId="solution-item-qty-input" />
                        <span className="w-10 shrink-0 text-xs text-muted-foreground">{it.unit}</span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{it.name} <span className="font-normal text-muted-foreground">{it.manufacturer}</span></div>
                          {it.reason ? <div className="truncate text-xs text-muted-foreground">{it.reason}</div> : null}
                        </div>
                        <span className="hidden w-24 text-right tabular text-muted-foreground sm:block">{fmtNOK(it.sales_price)}</span>
                        <span className="w-28 text-right tabular font-medium">{fmtNOK(it.qty * it.sales_price * (1 - (it.discount_pct || 0) / 100))}</span>
                        <div className="flex w-14 shrink-0 justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <button type="button" title="Swap product" onClick={() => setPicker({ itemId: it.id })} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="solution-item-swap-button"><Repeat className="h-3.5 w-3.5" /></button>
                          <button type="button" title="Remove" onClick={() => call(() => api.delete(`/solutions/${sol.id}/items/${it.id}`))} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive" data-testid="solution-item-delete-button"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <aside className="lg:sticky lg:top-6 lg:self-start">
            <Card className="p-5" data-testid="solution-totals">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Totals</div>
              <dl className="mt-3 space-y-1.5 text-sm">
                <Row label="Hardware" value={fmtNOK(totals.hardware)} />
                <Row label="Software" value={fmtNOK(totals.software)} />
                <Row label="Services" value={fmtNOK(totals.services)} />
                <div className="my-2 border-t" />
                <Row label="Total sales price" value={fmtNOK(totals.total_sales)} bold testId="solution-total-sales" />
                <Row label="Cost" value={fmtNOK(totals.total_cost)} />
                <Row label="Gross margin" value={fmtNOK(totals.gross_margin)} />
                <Row label="GM" value={fmtPct(totals.gm_pct)} bold className={totals.gm_pct < 30 ? "text-[hsl(var(--status-warn))]" : "text-[hsl(var(--status-ok))]"} testId="solution-gm-percent" />
              </dl>
              <div className="mt-4 text-xs text-muted-foreground">Longest lead time: <span className="tabular font-medium text-foreground">{totals.max_lead_time_weeks} weeks</span></div>
              <div className={cn("mt-4 rounded-xl border px-3 py-2 text-xs", approval?.required ? "border-[hsl(var(--status-warn)/0.3)] bg-[hsl(var(--status-warn)/0.06)]" : "border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.06)]")} data-testid="solution-approval">
                <span className="font-medium">{approval?.label}</span>{approval?.reason ? <div className="text-muted-foreground">{approval.reason}</div> : null}
              </div>
              <Button className="mt-4 w-full gap-2" variant="outline" onClick={() => setTab("calculation")} data-testid="solution-open-calculation-button">Open calculation <ArrowRight className="h-4 w-4" /></Button>
              <Button className="mt-2 w-full gap-2 bg-primary text-white hover:bg-[#ff8e76]" onClick={() => setTab("quote")} data-testid="solution-go-to-quote-button">Generate quote <ArrowRight className="h-4 w-4" /></Button>
              <div className="mt-3 text-[11px] text-muted-foreground">Prices are demo data.</div>
            </Card>
          </aside>
        </div>
      )}

      <ProductPicker
        open={!!picker}
        onOpenChange={(v) => !v && setPicker(null)}
        title={picker?.itemId ? "Swap product" : "Add product"}
        onPick={async (p) => {
          const pk = picker;
          setPicker(null);
          if (pk?.groupId) await call(() => api.post(`/solutions/${sol.id}/groups/${pk.groupId}/items`, { product_id: p.id, qty: 1 }), `${p.name} added`);
          else if (pk?.itemId) await call(() => api.patch(`/solutions/${sol.id}/items/${pk.itemId}`, { product_id: p.id }), `Swapped to ${p.name}`);
        }}
      />
    </div>
  );
}

function Row({ label, value, bold, className, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className={cn("text-muted-foreground", bold && "font-medium text-foreground")}>{label}</dt>
      <dd className={cn("tabular", bold ? "text-base font-semibold" : "", className)} data-testid={testId}>{value}</dd>
    </div>
  );
}
